import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { env } from '@/lib/env';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PayTransactionStatus = 'succeeded' | 'failed' | 'refunded' | 'disputed';

export interface PayTransaction {
  id: string;
  created_at: string;
  amount: number;               // øre
  fee: number;                  // platform fee øre
  net: number;                  // amount - fee øre
  status: PayTransactionStatus;
  customer_email: string | null;
  description: string | null;
  invoice_id: string | null;    // v2 invoice UUID (from metadata)
  booking_id: string | null;    // v2 booking UUID (from metadata)
  stripe_receipt_url: string | null;
  stripe_payment_intent_id: string | null;
  currency: string;             // DKK at v1
  clinic_id?: string | null;
}

export interface PayTransactionAggregates {
  total_volume: number;   // sum of amount for succeeded transactions (øre)
  total_fees: number;     // sum of fee for succeeded transactions (øre)
  count: number;          // total matching transactions
  refund_count: number;   // count of refunded transactions
}

export interface PayTransactionsFilters {
  since?: string;                     // ISO datetime
  until?: string;                     // ISO datetime
  status?: PayTransactionStatus[];
  limit?: number;                     // default 50, max 200
  cursor?: string;
}

export interface PayTransactionsResponse {
  items: PayTransaction[];
  cursor: string | null;
  has_more: boolean;
  aggregates: PayTransactionAggregates;
}

export interface MonthlyStatPoint {
  month: string;      // Short label e.g. "jan. '25"
  volume: number;     // Succeeded amount in øre
  fees: number;       // Succeeded fees in øre
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const payTransactionsQueryKey = (
  clinicId: string | null,
  filters: PayTransactionsFilters,
) => ['pay', 'transactions', clinicId, filters] as const;

export const payMonthlyStatsQueryKey = (clinicId: string | null) =>
  ['pay', 'monthly-stats', clinicId] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Build an authenticated fetch to a Supabase Edge Function. */
async function invokeEdgeFunctionGet<T>(
  functionName: string,
  params: Record<string, string | undefined>,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, v);
  }

  const resp = await fetch(
    `${env.VITE_SUPABASE_URL}/functions/v1/${functionName}?${qs.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );

  const json = (await resp.json()) as { ok: boolean; data?: T; error?: { message: string } };
  if (!resp.ok || !json.ok) {
    throw new Error(json.error?.message ?? `${functionName} failed (${resp.status})`);
  }
  return json.data as T;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of transactions from Clinio Pay via the
 * pay-list-transactions Edge Function. Also returns aggregate totals for
 * the current filter window.
 */
export function usePayTransactions(
  clinicId: string | null | undefined,
  filters: PayTransactionsFilters = {},
) {
  return useQuery({
    queryKey: payTransactionsQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PayTransactionsResponse> => {
      if (!clinicId) {
        return {
          items: [],
          cursor: null,
          has_more: false,
          aggregates: { total_volume: 0, total_fees: 0, count: 0, refund_count: 0 },
        };
      }

      const data = await invokeEdgeFunctionGet<PayTransactionsResponse>('pay-list-transactions', {
        clinic_id: clinicId,
        since:     filters.since,
        until:     filters.until,
        status:    filters.status && filters.status.length > 0
          ? filters.status.join(',')
          : undefined,
        limit:  filters.limit ? String(filters.limit) : undefined,
        cursor: filters.cursor,
      });

      return enrichStripePaymentIntentIds(data, clinicId);
    },
    staleTime: 30_000,
  });
}

/**
 * Monthly volume + fees for the last 12 months via Clinio Pay (pay-list-transactions).
 */
export function usePayMonthlyStats(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: payMonthlyStatsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<MonthlyStatPoint[]> => {
      if (!clinicId) return [];

      const since = new Date();
      since.setFullYear(since.getFullYear() - 1);
      since.setDate(1);
      since.setHours(0, 0, 0, 0);

      const data = await invokeEdgeFunctionGet<PayTransactionsResponse>(
        'pay-list-transactions',
        {
          clinic_id: clinicId,
          since: since.toISOString(),
          status: 'succeeded',
          limit: '200',
        },
      );

      const monthly: Record<string, MonthlyStatPoint> = {};
      for (let i = 11; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthly[key] = {
          month: d.toLocaleDateString('da-DK', { month: 'short', year: '2-digit' }),
          volume: 0,
          fees: 0,
        };
      }

      for (const txn of data.items) {
        if (txn.status !== 'succeeded') continue;
        const d = new Date(txn.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (monthly[key]) {
          monthly[key].volume += txn.amount;
          monthly[key].fees += txn.fee;
        }
      }

      return Object.values(monthly);
    },
    staleTime: 60_000,
  });
}

/**
 * Trigger a CSV export download via pay-export-csv Edge Function.
 * Returns a function that, when called, initiates the browser download.
 */
export function useExportPayTransactions() {
  return async (
    clinicId: string,
    filters: Pick<PayTransactionsFilters, 'since' | 'until' | 'status'> = {},
  ): Promise<void> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const qs = new URLSearchParams({ clinic_id: clinicId });
    if (filters.since) qs.set('since', filters.since);
    if (filters.until) qs.set('until', filters.until);
    if (filters.status && filters.status.length > 0) qs.set('status', filters.status.join(','));

    const resp = await fetch(
      `${env.VITE_SUPABASE_URL}/functions/v1/pay-export-csv?${qs.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ''}`,
          apikey: env.VITE_SUPABASE_ANON_KEY,
        },
      },
    );

    if (!resp.ok) {
      const json = await resp.json().catch(() => null) as { error?: { message?: string } } | null;
      throw new Error(json?.error?.message ?? `CSV export failed (${resp.status})`);
    }

    const blob = await resp.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;

    const cd = resp.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(cd);
    a.download = match?.[1] ?? `transaktioner-${new Date().toISOString().slice(0, 10)}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
}

// ── Formatting ────────────────────────────────────────────────────────────────

function asPaymentIntentId(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.startsWith('pi_') ? trimmed : null;
}

function paymentIntentFromText(value: string | null | undefined): string | null {
  const direct = asPaymentIntentId(value);
  if (direct) return direct;
  if (!value) return null;
  const match = value.match(/pi_[A-Za-z0-9]+/);
  return match ? match[0] : null;
}

function copenhagenDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' });
}

/**
 * Clinio Pay list often omits PaymentIntent IDs. Resolve them from v2 invoices
 * (id, invoice number, amount+date) and from receipt URLs.
 */
async function enrichStripePaymentIntentIds(
  data: PayTransactionsResponse,
  clinicId: string,
): Promise<PayTransactionsResponse> {
  const { data: invoices } = await supabase
    .from('invoices')
    .select('id, invoice_number, payment_reference, paid_at, total_cents')
    .eq('clinic_id', clinicId)
    .like('payment_reference', 'pi_%');

  const { data: payments } = await supabase
    .from('invoice_payments')
    .select('invoice_id, payment_reference, amount_cents, payment_date')
    .eq('clinic_id', clinicId)
    .like('payment_reference', 'pi_%');

  const byInvoiceId = new Map<string, string>();
  const byInvoiceNumber = new Map<string, string>();
  const byAmountDay = new Map<string, string[]>();

  const rememberAmountDay = (amount: number | null | undefined, day: string | null, pi: string) => {
    if (amount == null || !day) return;
    const key = `${amount}|${day}`;
    const list = byAmountDay.get(key) ?? [];
    if (!list.includes(pi)) list.push(pi);
    byAmountDay.set(key, list);
  };

  for (const row of invoices ?? []) {
    const pi = asPaymentIntentId(row.payment_reference as string | null);
    if (!pi) continue;
    byInvoiceId.set(row.id as string, pi);
    const number = row.invoice_number as string | null;
    if (number) byInvoiceNumber.set(number, pi);
    rememberAmountDay(row.total_cents as number | null, copenhagenDay(row.paid_at as string | null), pi);
  }

  for (const row of payments ?? []) {
    const pi = asPaymentIntentId(row.payment_reference as string | null);
    if (!pi) continue;
    if (row.invoice_id) byInvoiceId.set(row.invoice_id as string, pi);
    rememberAmountDay(row.amount_cents as number | null, copenhagenDay(row.payment_date as string | null), pi);
  }

  const uniqueAmountDay = (amount: number, createdAt: string): string | null => {
    const matches = byAmountDay.get(`${amount}|${copenhagenDay(createdAt)}`);
    return matches?.length === 1 ? matches[0] : null;
  };

  return {
    ...data,
    items: data.items.map((txn) => {
      const pi =
        asPaymentIntentId(txn.stripe_payment_intent_id) ??
        asPaymentIntentId(txn.id) ??
        paymentIntentFromText(txn.stripe_receipt_url) ??
        (txn.invoice_id ? byInvoiceId.get(txn.invoice_id) ?? null : null) ??
        (txn.description ? byInvoiceNumber.get(txn.description) ?? null : null) ??
        uniqueAmountDay(txn.amount, txn.created_at);

      return pi && pi !== txn.stripe_payment_intent_id
        ? { ...txn, stripe_payment_intent_id: pi }
        : txn;
    }),
  };
}

/** Dashboard URL for a PaymentIntent. Uses test mode and the connected account when known. */
export function stripePaymentDashboardUrl(
  paymentIntentId: string | null | undefined,
  stripeAccountId?: string | null,
  testMode?: boolean,
): string | null {
  const pi = asPaymentIntentId(paymentIntentId);
  if (!pi) return null;
  const mode = testMode ? 'test/' : '';
  if (stripeAccountId && stripeAccountId.startsWith('acct_')) {
    return `https://dashboard.stripe.com/${stripeAccountId}/${mode}payments/${pi}`;
  }
  return `https://dashboard.stripe.com/${mode}payments/${pi}`;
}

/** Format an amount in øre as a DKK currency string. */
export function formatPayMoney(ore: number, currency = 'DKK'): string {
  return (ore / 100).toLocaleString('da-DK', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });
}
